// DecisionPanel — Phase 9.
//
// Renders the pending-decision queue for a single conversation. The proposer
// inserts rows into `decisions_queue` whenever it fires a side-effect like
// `queue_refund_decision` or `queue_reprint_decision` (damage flow), or
// `address_change_review` (address-change orchestration).
//
// Each row gets two buttons:
//   - Approve (primary): POSTs status='approved'. Audit only — Kimi still
//     completes the actual refund or reprint in Shopify / ShipStation.
//   - Dismiss (secondary): POSTs status='cancelled' so the row is no longer
//     pending. Used when the customer recants or it turns out to be a false
//     positive from the classifier.
//
// Empty state: panel renders nothing at all (returns null) so the customer
// rail stays uncluttered when there's nothing to decide.

import { useState } from 'react';
import {
  useDecisions,
  useResolveDecision,
  DECISION_META,
  type DecisionRow,
  type DecisionKind,
} from '@/lib/hooks/useDecisions';

export interface DecisionPanelProps {
  conversationId: string;
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function chipColorForKind(kind: DecisionKind): string {
  switch (kind) {
    case 'refund':
      return 'leather';
    case 'reprint':
      return 'monstera';
    case 'address_change_review':
      return 'sky';
    case 'cancel_review':
      return 'clay';
    default:
      return 'stone';
  }
}

function DecisionRowItem({
  row,
  conversationId,
}: {
  row: DecisionRow;
  conversationId: string;
}) {
  const meta = DECISION_META[row.kind];
  const resolve = useResolveDecision(conversationId);
  // Track which side fired so we can show the right loading label without
  // disabling both buttons unnecessarily.
  const [pending, setPending] = useState<'approve' | 'dismiss' | null>(null);

  const onApprove = () => {
    setPending('approve');
    resolve.mutate(
      { id: row.id, status: 'approved' },
      { onSettled: () => setPending(null) },
    );
  };
  const onDismiss = () => {
    setPending('dismiss');
    resolve.mutate(
      { id: row.id, status: 'cancelled' },
      { onSettled: () => setPending(null) },
    );
  };

  if (row.status !== 'pending') {
    // Resolved rows render as a faded one-liner so Kimi still has a record
    // of what happened in this thread without it shouting.
    const resolvedLabel =
      row.status === 'approved'
        ? 'Approved'
        : row.status === 'denied'
          ? 'Denied'
          : 'Dismissed';
    return (
      <div className="decision-row decision-row--resolved">
        <span className={`decision-chip chip-${chipColorForKind(row.kind)}`}>
          {meta?.chipLabel ?? row.kind}
        </span>
        <span className="decision-resolved-label">
          {resolvedLabel}
          {row.order_name ? ` · ${row.order_name}` : ''}
          {row.resolved_at ? ` · ${formatRelative(row.resolved_at)}` : ''}
        </span>
      </div>
    );
  }

  return (
    <div className="decision-row">
      <div className="decision-row-head">
        <span className={`decision-chip chip-${chipColorForKind(row.kind)}`}>
          {meta?.chipLabel ?? row.kind}
        </span>
        {row.order_name ? (
          <span className="decision-order">{row.order_name}</span>
        ) : null}
        <span className="decision-age">{formatRelative(row.created_at)}</span>
      </div>
      {row.note ? <p className="decision-note">{row.note}</p> : null}
      <p className="decision-help">{meta?.help ?? ''}</p>
      <div className="decision-actions">
        <button
          type="button"
          className="btn primary sm"
          onClick={onApprove}
          disabled={pending !== null}
          title={meta?.help ?? ''}
        >
          {pending === 'approve' ? 'Approving…' : (meta?.verb ?? 'Approve')}
        </button>
        <button
          type="button"
          className="btn sm"
          onClick={onDismiss}
          disabled={pending !== null}
          title="Mark this decision as no longer needed"
        >
          {pending === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
}

export function DecisionPanel({ conversationId }: DecisionPanelProps) {
  const q = useDecisions(conversationId);
  const all = q.data ?? [];
  // Surface pending first, then a small history of recently resolved.
  const pending = all.filter((d) => d.status === 'pending');
  const resolved = all.filter((d) => d.status !== 'pending').slice(0, 3);

  if (q.isLoading) return null; // stay quiet on initial load
  if (pending.length === 0 && resolved.length === 0) return null;

  return (
    <section className="decision-panel" aria-label="Pending decisions">
      <div className="eyebrow">
        {pending.length > 0
          ? `Decisions waiting · ${pending.length}`
          : 'Recent decisions'}
      </div>
      {pending.map((row) => (
        <DecisionRowItem key={row.id} row={row} conversationId={conversationId} />
      ))}
      {resolved.length > 0 ? (
        <div className="decision-resolved-list">
          {resolved.map((row) => (
            <DecisionRowItem
              key={row.id}
              row={row}
              conversationId={conversationId}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
